import { PropertyType } from "@/tailwind/tailwindUtils";
import type { ClassTypeWithPropertyType } from "@/tailwind/tailwindUtils";
import type { ColorGroup, ColorToken } from "../types";
import {
  getCssColorValue,
  hexToRgbSpaceSeparated,
  isCssColorFunction,
  normalizeColorValue,
} from "@/components/RightStyle/TailwindStylingPanel/components/input/color-picker/utils/colorFormatUtils";

export function generateDesignTokenClasses(
  colorPath: string,
  colorValue: string,
): ClassTypeWithPropertyType[] {
  const classes: ClassTypeWithPropertyType[] = [];

  const cssValue = getCssColorValue(colorValue);

  classes.push({
    tailwind: `bg-${colorPath}`,
    css: `background-color: ${cssValue};`,
    propertyType: PropertyType.BACKGROUND_COLOR,
    isDesignToken: true,
  });

  classes.push({
    tailwind: `text-${colorPath}`,
    css: `color: ${cssValue};`,
    propertyType: PropertyType.TEXT_COLOR,
    isDesignToken: true,
  });

  classes.push({
    tailwind: `border-${colorPath}`,
    css: `border-color: ${cssValue};`,
    propertyType: PropertyType.BORDER_COLOR,
    isDesignToken: true,
  });

  const rgbSpaceSeparated = isCssColorFunction(colorValue)
    ? colorValue
    : hexToRgbSpaceSeparated(normalizeColorValue(colorValue));

  classes.push({
    tailwind: `from-${colorPath}`,
    css: `--tw-gradient-from: ${rgbSpaceSeparated} var(--tw-gradient-from-position); --tw-gradient-to: ${rgbSpaceSeparated} / 0 var(--tw-gradient-to-position); --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);`,
    propertyType: PropertyType.FROM_COLOR,
    isDesignToken: true,
  });

  classes.push({
    tailwind: `to-${colorPath}`,
    css: `--tw-gradient-to: ${rgbSpaceSeparated} var(--tw-gradient-to-position);`,
    propertyType: PropertyType.TO_COLOR,
    isDesignToken: true,
  });

  classes.push({
    tailwind: `via-${colorPath}`,
    css: `--tw-gradient-to: ${rgbSpaceSeparated} / 0 var(--tw-gradient-to-position); --tw-gradient-stops: var(--tw-gradient-from), ${rgbSpaceSeparated} var(--tw-gradient-via-position), var(--tw-gradient-to);`,
    propertyType: PropertyType.VIA_COLOR,
    isDesignToken: true,
  });

  return classes;
}

export function generateAllTokenClasses(
  lightGroups: ColorGroup[],
  lightUngroupedColors: ColorToken[],
  darkGroups?: ColorGroup[],
  darkUngroupedColors?: ColorToken[],
): ClassTypeWithPropertyType[] {
  const newTokenClasses: ClassTypeWithPropertyType[] = [];

  for (const group of lightGroups) {
    for (const color of group.colors) {
      const colorPath =
        color.name === "default" ? group.name : `${group.name}/${color.name}`;
      const tokenClasses = generateDesignTokenClasses(colorPath, color.color);
      newTokenClasses.push(...tokenClasses);
    }
  }

  for (const color of lightUngroupedColors) {
    const colorPath = color.name;
    const tokenClasses = generateDesignTokenClasses(colorPath, color.color);
    newTokenClasses.push(...tokenClasses);
  }

  if (darkGroups) {
    for (const group of darkGroups) {
      for (const color of group.colors) {
        const darkColorPath =
          color.name === "default"
            ? `dark-${group.name}`
            : `dark-${group.name}/${color.name}`;
        const tokenClasses = generateDesignTokenClasses(
          darkColorPath,
          color.color,
        );
        newTokenClasses.push(...tokenClasses);
      }
    }
  }

  if (darkUngroupedColors) {
    for (const color of darkUngroupedColors) {
      const darkColorPath = `dark-${color.name}`;
      const tokenClasses = generateDesignTokenClasses(
        darkColorPath,
        color.color,
      );
      newTokenClasses.push(...tokenClasses);
    }
  }

  return newTokenClasses;
}
